import {
  EuiFormRow,
  EuiInputPopover,
  EuiSelectable,
  EuiSelectableOption,
} from "@inscopix/ideas-eui";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { chain, get } from "lodash";
import { useToolSpecContext } from "providers/ToolSpecProvider/ToolSpecProvider";
import { useMemo, useState } from "react";
import { ToolMaturity } from "types/constants";

export type FieldToolProps = {
  autoFocus?: boolean;
  label?: string;
  value: ToolSpec | undefined;
  onChange: (
    toolSpec: ToolSpec | undefined,
    toolVersionId: number | undefined,
  ) => void;
};

export const FieldTool = ({
  autoFocus = false,
  label = "Analysis",
  value,
  onChange,
}: FieldToolProps) => {
  const { availableToolSpecs } = useToolSpecContext();

  const initialOptions: EuiSelectableOption[] = useMemo(() => {
    const validSpecs = availableToolSpecs
      // don't show deprecated tools as options
      .filter(
        ({ spec, maturity }) =>
          maturity !== ToolMaturity.DEPRECATED && spec.name !== undefined,
      );

    return chain(validSpecs)
      .groupBy(({ spec }) => spec.toolkit)
      .entries()
      .flatMap(([toolkit, specs]) => [
        { label: toolkit, isGroupLabel: true },
        ...specs.map(({ spec, toolVersionId }) => ({
          label: spec.name,
          data: { spec, toolVersionId },
        })),
      ])
      .value();
  }, [availableToolSpecs]);

  const [isOpen, setIsOpen] = useState(autoFocus);
  const [options, setOptions] = useState(initialOptions);
  const [inputValue, setInputValue] = useState(value?.name ?? "");

  return (
    <EuiFormRow label={label} fullWidth>
      <EuiSelectable
        aria-label="Select analysis"
        options={options}
        renderOption={(option) => {
          return (
            <span>
              <b>{option.label}</b> {get(option, "spec.version")}
              <br />
              {get(option, "spec.help")}
            </span>
          );
        }}
        onChange={(newOptions, event, changedOption) => {
          setOptions(newOptions);
          if (changedOption.checked === "on") {
            setInputValue(changedOption.label);
            onChange(
              get(changedOption, "data.spec") as ToolSpec,
              get(changedOption, "data.toolVersionId") as number,
            );
            // setIsSearching(false);
            setIsOpen(false);
          } else {
            setInputValue("");
            onChange(undefined, undefined);
          }
        }}
        singleSelection
        searchable
        searchProps={{
          autoFocus: autoFocus,
          value: inputValue,
          onChange: (value) => {
            setInputValue(value);
            // setIsSearching(true);
          },
          placeholder: "Select an analysis...",
          onKeyDown: (event) => {
            if (event.key === "Tab") return setIsOpen(false);
            if (event.key !== "Escape") return setIsOpen(true);
          },
          onClick: () => setIsOpen(true),
          onFocus: () => setIsOpen(true),
        }}
        listProps={{
          style: { maxHeight: 400, overflow: "scroll" },
          textWrap: "wrap",
          isVirtualized: false,
        }}
      >
        {(list, search) => (
          <EuiInputPopover
            autoFocus={autoFocus}
            fullWidth
            closePopover={() => {
              setIsOpen(false);
              setInputValue(value?.name ?? "");
            }}
            disableFocusTrap
            closeOnScroll
            isOpen={isOpen}
            input={search ?? ""}
            panelPaddingSize="none"
          >
            {list}
          </EuiInputPopover>
        )}
      </EuiSelectable>
    </EuiFormRow>
  );
};
